import { useReceptionLocale } from '../../reception/locale';
import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReceptionLayoutContext, useReceptionLayout } from './ReceptionLayoutContext';
import { getReceptionWorkflowGroup, receptionWorkflowGroups, type ReceptionWorkflowGroup } from './receptionWorkflowGroups';

/** Groups existing route pages without replacing their navigation, data or shell. */
export default function ReceptionWorkflowTabs({ group }: { group: ReceptionWorkflowGroup }) {
  const { t: receptionText, format: receptionFormat } = useReceptionLocale();

  const location = useLocation();
  const layout = useReceptionLayout();
  const { setTitle } = layout;
  const workflow = receptionWorkflowGroups[group];
  const active = getReceptionWorkflowGroup(location) === group;
  const destinations = React.useRef<Record<string, string>>({});

  React.useEffect(() => {
    if (active && location.pathname !== '/reception/waiting') {
      destinations.current[location.pathname] = `${location.pathname}${location.search}${location.hash}`;
    }
  }, [active, location.pathname, location.search, location.hash]);
  React.useEffect(() => {
    if (active) setTitle(workflow.title);
  }, [active, setTitle, workflow.title]);

  // The grouped title owns the shell identity; pages still control padding and scroll readiness.
  const groupedLayout = React.useMemo(() => ({ ...layout, setTitle: () => {} }), [layout]);

  return (
    <ReceptionLayoutContext.Provider value={active ? groupedLayout : layout}>
      <div className="aa-reception-workflow bg-[#f8fafc] text-slate-950">
        {active ? <header className="border-b border-slate-200/80 px-4 pt-5 lg:px-8">
          <h1 className="m-0 text-3xl font-black tracking-tight md:text-4xl">{receptionText(workflow.title)}</h1>
          <nav aria-label={receptionFormat("{0} tabs", [receptionText(workflow.title)])} className="mt-4 flex gap-2 overflow-x-auto pb-3">
            {workflow.tabs.map((tab) => {
              const pathname = tab.to.split('?')[0];
              const selected = location.pathname === pathname;
              return (
                <Link
                  key={pathname}
                  to={selected ? `${location.pathname}${location.search}${location.hash}` : destinations.current[pathname] ?? tab.to}
                  aria-current={selected ? 'page' : undefined}
                  className={`shrink-0 rounded-xl px-4 py-2 text-sm font-bold no-underline transition ${selected ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-violet-50 hover:text-violet-700'}`}
                >{receptionText(tab.label)}</Link>
              );
            })}
          </nav>
        </header> : null}
        <Outlet />
      </div>
    </ReceptionLayoutContext.Provider>
  );
}
